import { apiFetch } from "../api/client";
import React, { useCallback, useEffect, useMemo, useState } from "react";

const API = process.env.REACT_APP_API_URL || "https://api.wirelesspress.org";

function profile() {
  for (const key of ["user", "currentUser", "profile"]) {
    try {
      const value = JSON.parse(localStorage.getItem(key) || "null");
      if (value && typeof value === "object") return value;
    } catch (_) {}
  }
  return {};
}

function headers(json = false) {
  const token = localStorage.getItem("access_token") || localStorage.getItem("token") || localStorage.getItem("authToken") || "";
  return {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(json ? { "Content-Type": "application/json" } : {}),
  };
}

async function api(path, options = {}) {
  const response = await apiFetch(`${path}`, options);
  const body = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(body.detail || `Request failed with HTTP ${response.status}`);
  return body;
}

const panel = { background: "#101923", border: "1px solid #263747", borderRadius: 14, padding: 20, marginBottom: 18 };
const input = { width: "100%", boxSizing: "border-box", background: "#0a1118", color: "#eef5fb", border: "1px solid #31485b", borderRadius: 8, padding: "10px 12px" };
const button = { border: 0, borderRadius: 8, padding: "9px 13px", color: "white", background: "#285d7a", cursor: "pointer", fontWeight: 700 };
const initialContent = JSON.stringify({ schema_version: 1, controls: {}, metadata: {} }, null, 2);

export default function PolicyLifecyclePage() {
  const user = useMemo(profile, []);
  const role = user.role || localStorage.getItem("role") || "";
  const companyId = user.company_id || localStorage.getItem("company_id") || "";
  const isAdmin = ["super_admin", "company_admin"].includes(role);
  const [policies, setPolicies] = useState([]);
  const [selected, setSelected] = useState(null);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [platform, setPlatform] = useState("all");
  const [scope, setScope] = useState("tenant");
  const [targetCompany, setTargetCompany] = useState(companyId);
  const [content, setContent] = useState(initialContent);
  const [summary, setSummary] = useState("Initial policy version");
  const [publish, setPublish] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const load = useCallback(async () => {
    try {
      setError("");
      const rows = await api("/policy-lifecycle/policies?include_retired=true", { headers: headers() });
      setPolicies(Array.isArray(rows) ? rows : []);
      if (selected) {
        const detail = await api(`/policy-lifecycle/policies/${selected.policy_uuid}`, { headers: headers() });
        setSelected(detail);
      }
    } catch (err) { setError(err.message); }
  }, [selected?.policy_uuid]);

  useEffect(() => { load(); }, [load]);

  async function mutate(path, payload) {
    setBusy(true); setError(""); setNotice("");
    try {
      const result = await api(path, { method: "POST", headers: headers(true), body: JSON.stringify(payload) });
      await load();
      return result;
    } catch (err) { setError(err.message); return null; }
    finally { setBusy(false); }
  }

  function parsedContent() {
    try { return JSON.parse(content); }
    catch (_) { setError("Policy content must be valid JSON."); return null; }
  }

  async function createPolicy(event) {
    event.preventDefault();
    const value = parsedContent(); if (!value) return;
    const result = await mutate("/policy-lifecycle/policies", {
      name, description, platform_scope: platform, scope_type: scope,
      ...(role === "super_admin" && scope === "tenant" ? { company_id: Number(targetCompany) } : {}),
      policy_content: value, change_summary: summary, publish,
    });
    if (result) { setNotice(`Policy created at version ${result.version_number}.`); setName(""); }
  }

  async function addVersion() {
    if (!selected) return;
    const value = parsedContent(); if (!value) return;
    const result = await mutate(`/policy-lifecycle/policies/${selected.policy_uuid}/versions`, {
      base_version_number: selected.current_version_number,
      policy_content: value, change_summary: summary, publish,
    });
    if (result) setNotice(`Immutable version ${result.version_number} created.`);
  }

  async function selectPolicy(policy) {
    try {
      setError("");
      const detail = await api(`/policy-lifecycle/policies/${policy.policy_uuid}`, { headers: headers() });
      setSelected(detail);
      const current = detail.versions?.find((item) => item.version_number === detail.current_version_number);
      if (current) setContent(JSON.stringify(current.policy_content, null, 2));
    } catch (err) { setError(err.message); }
  }

  async function publishVersion(version) {
    if (!selected) return;
    const result = await mutate(`/policy-lifecycle/policies/${selected.policy_uuid}/versions/${version.version_number}/publish`, {
      expected_current_version: selected.current_version_number,
      change_summary: `Publish snapshot from version ${version.version_number}`,
    });
    if (result) setNotice(`Published immutable version ${result.version_number}.`);
  }

  async function retire() {
    if (!selected) return;
    const result = await mutate(`/policy-lifecycle/policies/${selected.policy_uuid}/retire`, { reason: "Administrative policy retirement" });
    if (result) setNotice("Policy retired; all versions remain preserved.");
  }

  return (
    <main style={{ color: "#eef5fb", padding: 24, maxWidth: 1400, margin: "0 auto" }}>
      <h1 style={{ marginBottom: 6 }}>Policy Lifecycle</h1>
      <p style={{ color: "#a9bbca", marginTop: 0 }}>Create tenant-isolated policies, maintain append-only versions, verify SHA-256 integrity, and publish without rewriting history.</p>
      {error && <div style={{ ...panel, borderColor: "#b42318", color: "#ffb4ab" }}>{error}</div>}
      {notice && <div style={{ ...panel, borderColor: "#16815d", color: "#9ce4c8" }}>{notice}</div>}

      {isAdmin && <section style={panel}>
        <h2>Create policy</h2>
        <form onSubmit={createPolicy} style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))", gap: 12 }}>
          <input required value={name} onChange={(e) => setName(e.target.value)} placeholder="Policy name" style={input} />
          <input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Description" style={input} />
          <select value={platform} onChange={(e) => setPlatform(e.target.value)} style={input}><option value="all">All platforms</option><option value="windows">Windows</option><option value="macos">macOS</option></select>
          {role === "super_admin" && <select value={scope} onChange={(e) => setScope(e.target.value)} style={input}><option value="tenant">Tenant policy</option><option value="global">Global policy</option></select>}
          {role === "super_admin" && scope === "tenant" && <input required type="number" min="1" value={targetCompany} onChange={(e) => setTargetCompany(e.target.value)} placeholder="Tenant company ID" style={input} />}
          <input value={summary} onChange={(e) => setSummary(e.target.value)} placeholder="Change summary" style={input} />
          <label style={{ padding: 10 }}><input type="checkbox" checked={publish} onChange={(e) => setPublish(e.target.checked)} /> Publish immediately</label>
          <button disabled={busy} style={{ ...button, background: "#16815d" }}>Create policy</button>
        </form>
      </section>}

      <section style={{ display: "grid", gridTemplateColumns: "minmax(300px, 0.8fr) minmax(420px, 1.4fr)", gap: 18 }}>
        <div style={panel}>
          <h2>Policies</h2>
          {policies.map((policy) => <button key={policy.policy_uuid} onClick={() => selectPolicy(policy)} style={{ ...button, width: "100%", textAlign: "left", marginBottom: 8, background: selected?.policy_uuid === policy.policy_uuid ? "#16815d" : "#1c3040" }}>
            {policy.name}<br /><small>{policy.scope_type} · {policy.platform_scope} · {policy.status} · latest v{policy.current_version_number}</small>
          </button>)}
          {!policies.length && <p style={{ color: "#a9bbca" }}>No canonical policies.</p>}
        </div>

        <div>
          <section style={panel}>
            <h2>{selected ? selected.name : "Version editor"}</h2>
            <textarea value={content} onChange={(e) => setContent(e.target.value)} rows="16" style={{ ...input, fontFamily: "monospace" }} />
            {selected && isAdmin && selected.status !== "retired" && <div style={{ display: "flex", gap: 10, marginTop: 12, flexWrap: "wrap" }}>
              <button disabled={busy} onClick={addVersion} style={button}>Create next version</button>
              <button disabled={busy} onClick={retire} style={{ ...button, background: "#8b3a3a" }}>Retire policy</button>
            </div>}
          </section>

          {selected && <section style={panel}>
            <h2>Immutable version history</h2>
            {(selected.versions || []).map((version) => <div key={version.version_uuid} style={{ borderBottom: "1px solid #31485b", padding: "12px 0" }}>
              <strong>v{version.version_number} · {version.publication_state}</strong>
              <div style={{ color: "#a9bbca" }}>{version.change_summary}</div>
              <code style={{ fontSize: 12 }}>{version.content_sha256}</code>
              {isAdmin && selected.status !== "retired" && version.publication_state === "draft" && <button disabled={busy} onClick={() => publishVersion(version)} style={{ ...button, marginLeft: 12 }}>Publish as new snapshot</button>}
            </div>)}
          </section>}
        </div>
      </section>
    </main>
  );
}
